import { useCallback, useState } from 'react';

import { EMPTY_OBJECT } from '../helpers/object/empty-object';

type CustomFormProps = {
    initialValues: {
        [k: string]: any;
    };
    onSubmit: (values: { [k: string]: any }) => void;
    schema: any;
};

type Error = {
    [k: string]: {
        name: string;
        message: string;
    } | null;
};

export const useCustomForm = ({ initialValues, onSubmit, schema }: CustomFormProps) => {
    const [values, setValues] = useState(initialValues || EMPTY_OBJECT);
    const [errors, setErrors] = useState<Error>(EMPTY_OBJECT);
    const [failedToSubmit, setFailedToSubmit] = useState<boolean>(false);

    const handleChange = useCallback((val: any, id: string) => {
        setValues((state) => ({ ...state, [id]: val }));
        setErrors((state) => ({ ...state, [id]: null }));
    }, []);

    const handleValidation = async () => {
        try {
            await schema.validate(values, {
                abortEarly: false,
            });
            return false;
        } catch (errsObj: any) {
            const err: Error = {};

            errsObj.inner?.forEach((element: any) => {
                err[element.path] = {
                    name: element.path,
                    message: element.message,
                };
            });

            setErrors(err);

            return err;
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        if (event) event.preventDefault();
        setFailedToSubmit(false);
        const validationErrors = await handleValidation();

        if (validationErrors) {
            setFailedToSubmit(true);
            return false;
        }
        await onSubmit(values);
    };

    return {
        values,
        setValues,
        handleValidation,
        errors,
        updateErrors: setErrors,
        handleChange,
        handleSubmit,
        failedToSubmit,
    };
};
